import React, { useState } from 'react';
import type { CategoryRecord, TaskRecord } from '../api';
import { describeDate, isOverdue, isToday } from '../utils/date';

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Urgent',
  2: 'High',
  3: 'Normal',
  4: 'Low',
};

interface Props {
  tasks: TaskRecord[];
  categories: CategoryRecord[];
  statusFilter: string | null;
  categoryFilter: string | null;
  statusCounts: Record<string, number>;
  onStatusFilter: (status: string | null) => void;
  onCategoryFilter: (category: string | null) => void;
  onToggleTask: (task: TaskRecord) => void;
  onSelectTask: (task: TaskRecord) => void;
  onAddTask: (title: string) => void;
}

export function TaskSidebar({
  tasks,
  categories,
  statusFilter,
  categoryFilter,
  statusCounts,
  onStatusFilter,
  onCategoryFilter,
  onToggleTask,
  onSelectTask,
  onAddTask,
}: Props) {
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && newTaskTitle.trim()) {
      onAddTask(newTaskTitle.trim());
      setNewTaskTitle('');
    }
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h2>Tasks</h2>

        <div className="filter-chips">
          <button className={`chip ${!statusFilter ? 'active' : ''}`} onClick={() => onStatusFilter(null)}>
            All <span className="count">{statusCounts.all}</span>
          </button>
          <button className={`chip ${statusFilter === 'pending' ? 'active' : ''}`} onClick={() => onStatusFilter(statusFilter === 'pending' ? null : 'pending')}>
            Pending <span className="count">{statusCounts.pending}</span>
          </button>
          <button className={`chip ${statusFilter === 'in_progress' ? 'active' : ''}`} onClick={() => onStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}>
            Active <span className="count">{statusCounts.in_progress}</span>
          </button>
          <button className={`chip ${statusFilter === 'done' ? 'active' : ''}`} onClick={() => onStatusFilter(statusFilter === 'done' ? null : 'done')}>
            Done <span className="count">{statusCounts.done}</span>
          </button>
          {statusCounts.blocked > 0 && (
            <button className={`chip ${statusFilter === 'blocked' ? 'active' : ''}`} onClick={() => onStatusFilter(statusFilter === 'blocked' ? null : 'blocked')}>
              Blocked <span className="count">{statusCounts.blocked}</span>
            </button>
          )}
        </div>

        {categories.length > 0 && (
          <div className="filter-chips" style={{ marginTop: 8 }}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`chip ${categoryFilter === category.name ? 'active' : ''}`}
                onClick={() => onCategoryFilter(categoryFilter === category.name ? null : category.name)}
                style={categoryFilter === category.name ? { borderColor: category.color || 'var(--accent)' } : {}}
              >
                {category.icon} {category.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="task-list">
        {tasks.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No tasks match your filters.
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              className={`task-card ${task.status}`}
              style={isOverdue(task.dueDate) && task.status !== 'done' ? { borderLeft: '3px solid var(--danger)' } : undefined}
              onClick={() => onSelectTask(task)}
            >
              <div
                className={`task-checkbox ${task.status === 'done' ? 'checked' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleTask(task);
                }}
              />
              <div className="task-content">
                <div className="task-title">{task.title}</div>
                <div className="task-meta">
                  <span className={`priority-dot priority-${task.priority}`} />
                  <span>{PRIORITY_LABELS[task.priority] || 'Normal'}</span>
                  {task.categoryName && (
                    <span className="category-badge">
                      {task.categoryIcon} {task.categoryName}
                    </span>
                  )}
                  {task.dueDate && (
                    <span style={isOverdue(task.dueDate) ? { color: 'var(--danger)' } : isToday(task.dueDate) ? { color: 'var(--accent)' } : undefined}>
                      {describeDate(task.dueDate)}
                    </span>
                  )}
                  {task.scheduledStart && (
                    <span style={{ color: 'var(--accent)' }}>Scheduled</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="add-task-bar">
        <input
          type="text"
          className="add-task-input"
          placeholder="Capture an inbox task... (press Enter)"
          value={newTaskTitle}
          onChange={(event) => setNewTaskTitle(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
}
